import {FormState, FormStateWithIssues} from '@raison/form-models';
import {useLocationState} from '@raison/navigation';
import {ErroneousServerResult} from '@raison/network-facade';
import {TransactionId} from '@raison/types';
import {RootLayout, RsnAmount, RsnButton, RsnDialog, RsnInput, RsnMessage, RsnSkeleton} from '@raison/ui-kit';
import {useQuery} from '@tanstack/react-query';
import * as Schema from 'effect/Schema';
import React, {useReducer} from 'react';
import {useNavigate} from 'react-router';
import {useCurrencyFormat} from '~/entities/currencies';
import {OperationDetail, OperationInfo} from '~/entities/operations-fiat';
import {LabelWithInfo} from '~/processes/fiat-operations/ui/LabelWithInfo';
import {OperableAccount, OperableBank} from '~/redux/finances/domain';
import {useSepaBankInfo} from '../../api/withdraw/sepaBankInfo';
import {useSepaRequisitesOptions} from '../../api/withdraw/sepaRequisites';
import {useWithdrawFeeOptions} from '../../api/withdraw/withdrawFee';
import {useWithdrawSepaOrderMutation} from '../../api/withdraw/withdrawSepaOrder';
import {TotalReceiveDialog} from '../Shared/TotalReceiveDialog';

type WithdrawSepaFormState = ReturnType<typeof WithdrawSepaFormState>;
const WithdrawSepaFormState = ({iban}: {iban: string}) =>
    FormState.from({
        iban: iban,
    });

type Values = FormState.inferValues<WithdrawSepaFormState>;

export type SepaRequisitesProps = {
    amount: RsnAmount;
    account: OperableAccount;
    onSuccess: (id: TransactionId) => void;
    onProblem: (problem: ErroneousServerResult) => void;
    method: OperableBank;
};

export function WithdrawSepaRequisites({account, method, amount, onSuccess, onProblem}: SepaRequisitesProps) {
    const navigate = useNavigate();

    const {data: {value: requisites} = {}, isLoading: isRequisitesLoading} = useQuery(
        useSepaRequisitesOptions(account.id)
    );

    const [form, dispatchForm] = useReducer(
        FormState.reduce<Values>,
        {iban: requisites?.iban || ''},
        WithdrawSepaFormState
    );

    const {format: formatAsSubject} = useCurrencyFormat(account);

    const {data: {value: fee} = {}, isLoading: isFeeLoading} = useQuery(
        useWithdrawFeeOptions({
            accountId: account.id,
            methodId: method?.id,
            amount: RsnAmount.toString(amount) || '0',
        })
    );

    const {
        isDebouncing: isIbanInfoDebouncing,
        data: {value: ibanData, problem: ibanErrors} = {},
        isLoading: isIbanInfoLoading,
    } = useSepaBankInfo(account.id, form.fields.iban.value);

    const t = {
        feeLabel: 'Fee',
        amountLabel: 'Total amount',
        totalLabel: (() => {
            if (fee == null) {
                return 'Total';
            }
            return fee.commissionAbove ? 'Total spend' : 'Total receive';
        })(),
        nameLabel: 'Beneficiary name',
        ibanLabel: 'IBAN in your bank',
        bicLabel: 'BIC',
        bankNameLabel: 'Bank name',
        required: 'This field is required',
    };

    const formWithIssues = FormStateWithIssues.fromServerIssues(form, ibanErrors ?? {});

    const {mutateAsync, isPending: isMutationPending} = useWithdrawSepaOrderMutation(account.id);

    const {fields, isSubmitDisabled, onSubmit} = FormState.selectFormView(formWithIssues, {
        dispatch: dispatchForm,
        async submit() {
            const {value, problem} = await mutateAsync({
                iban: fields.iban.value,
                amount: RsnAmount.toString(amount),
            });
            if (problem) {
                onProblem(problem);
                return false;
            }
            onSuccess(value.id as TransactionId);
            return true;
        },
    });

    const [dialog, setDialog] = useLocationState('withdraw-sepa-requisites-dialog', Schema.Literal('total'));
    const closeDialog = () => navigate(-1);

    const canSubmit =
        !isIbanInfoLoading && !isIbanInfoDebouncing && !isSubmitDisabled && !isMutationPending && !isFeeLoading;

    return (
        <RootLayout title={'EU bank withdraw'}>
            <form className={'flex flex-1 flex-col gap-6 p-4'}>
                <RsnMessage.FootnoteV2>
                    Receiving bank account must be named on the account holder of Raison account, otherwise your bank
                    will return the payment.
                </RsnMessage.FootnoteV2>

                <OperationInfo label={t.nameLabel}>
                    {isRequisitesLoading ? <RsnSkeleton width={100} height={20} /> : requisites?.beneficiary_name}
                </OperationInfo>

                <RsnInput label={t.ibanLabel} {...fields.iban} />
                <div className={'mt-auto flex flex-col gap-4'}>
                    <OperationDetail.Layout>
                        <OperationDetail label={t.bicLabel}>{ibanData?.bank ?? '—'}</OperationDetail>
                        <OperationDetail label={t.bankNameLabel}>{ibanData?.bban ?? '—'}</OperationDetail>
                    </OperationDetail.Layout>
                    <OperationDetail.Layout>
                        {isFeeLoading ? (
                            <>
                                <OperationDetail label={t.feeLabel}>
                                    <RsnSkeleton width={60} height={16} />
                                </OperationDetail>
                                <OperationDetail label={t.amountLabel}>
                                    <RsnSkeleton width={60} height={16} />
                                </OperationDetail>
                                <OperationDetail label={t.totalLabel}>
                                    <RsnSkeleton width={60} height={16} />
                                </OperationDetail>
                            </>
                        ) : (
                            <>
                                <OperationDetail label={t.feeLabel}>{formatAsSubject(fee?.fee ?? 0)}</OperationDetail>
                                <OperationDetail label={t.amountLabel}>
                                    {formatAsSubject(fee?.inputAmount ?? 0)}
                                </OperationDetail>
                                <OperationDetail.Total
                                    label={
                                        <LabelWithInfo onClick={() => setDialog('total')}>{t.totalLabel}</LabelWithInfo>
                                    }
                                >
                                    {formatAsSubject(fee?.total ?? 0)}
                                </OperationDetail.Total>
                            </>
                        )}
                    </OperationDetail.Layout>
                    <RsnButton type={'submit'} disabled={!canSubmit} onClick={onSubmit}>
                        Submit
                    </RsnButton>
                </div>
            </form>
            <RsnDialog.Controller onClose={closeDialog}>
                {dialog === 'total' && <TotalReceiveDialog />}
            </RsnDialog.Controller>
        </RootLayout>
    );
}
